import { useState } from "react";

import { CoachingExplanation }
  from "@/components/coaching/CoachingExplanation";
import { CoachingMetric, CoachingSectionCard }
  from "@/components/coaching/CoachingSectionCard";
import {
  applyPlanAdaptation,
  rejectPlanAdaptation,
} from "@/api/planAdaptationApi";
import { normalizePlanChangePreview }
  from "@/lib/coachingActionGuard";
import { notifyActivePlanChanged } from "@/lib/activePlanSync";
import {
  formatAdaptationAction,
  formatAdaptationScope,
  formatAdaptationStatus,
  formatConfidence,
  formatDate,
} from "@/lib/coachingFormatters";

const INCOMPLETE_APPLY_MESSAGE =
  "We couldn't finish applying this update. Your plan has not been confirmed as fully adjusted.";

const APPLIED_LABELS = {
  APPLIED: "Applied",
  PENDING: "Pending confirmation",
  PENDING_CONFIRMATION: "Pending confirmation",
  APPLYING: "Not fully adjusted",
  PARTIAL: "Not fully adjusted",
  FAILED: "Not fully adjusted",
};

const ESCALATION_STATUSES = new Set([
  "CHAT2_REASSESSMENT_REQUIRED",
  "PLAN_SUSPENDED_FOR_SAFETY",
  "NO_SAFE_ADAPTATION_AVAILABLE",
]);

const INCOMPLETE_STATUSES = new Set(["APPLYING", "PARTIAL", "FAILED"]);

const notifyPlanChanged = () => {
  notifyActivePlanChanged({ source: "plan-adaptation" });
};

const formatDuration = (minutes) => {
  if (typeof minutes !== "number" || !Number.isFinite(minutes)) {
    return null;
  }
  return `${minutes} min`;
};

const PreviewSide = ({ side, label, testIdPrefix }) => {
  if (!side) {
    return null;
  }
  const duration = formatDuration(side.durationMinutes);
  return (
    <div data-testid={`${testIdPrefix}-${label.toLowerCase()}`} className="min-w-0">
      <p className="text-[10px] uppercase tracking-[0.2em] text-dark-text-muted">
        {label}
      </p>
      <p className="mt-1 text-sm font-medium text-dark-text-primary break-words">
        {side.sessionTitle}
      </p>
      {duration && (
        <p className="text-sm text-dark-text-secondary">{duration}</p>
      )}
      {side.difficultyLabel
        && side.difficultyLabel !== side.sessionTitle && (
        <p className="text-sm text-dark-text-secondary break-words">
          {side.difficultyLabel}
        </p>
      )}
    </div>
  );
};

export const PlanChangeCard = ({ adaptation, timezone,
                                 explanation, error, onResolved }) => {
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const noAdaptation = !error
    && (!adaptation || adaptation.no_adaptation === true
         || !adaptation.adaptation_id);
  const incomplete = Boolean(
    adaptation && INCOMPLETE_STATUSES.has(adaptation.applied_status)
  );
  const pending = adaptation && (
    adaptation.applied_status === "PENDING"
    || adaptation.applied_status === "PENDING_CONFIRMATION"
    || incomplete
  );
  const applied = adaptation && adaptation.applied_status === "APPLIED";
  const keepAsPlanned = adaptation?.status === "NO_CHANGE_REQUIRED"
    || (Array.isArray(adaptation?.actions)
        && adaptation.actions.length > 0
        && adaptation.actions.every((a) => a.action === "KEEP_AS_PLANNED"));
  const escalation = Boolean(
    adaptation?.block_regeneration_required
    || adaptation?.reassessment_required
    || adaptation?.safety_suspension
    || ESCALATION_STATUSES.has(adaptation?.status)
  );
  const appliedAutomatically = Boolean(adaptation?.applied_automatically);
  const needsConfirm = pending && !keepAsPlanned && !escalation && !appliedAutomatically;
  const planChangePreview = normalizePlanChangePreview(
    adaptation?.plan_change_preview,
  );
  const recommendationHeadline = adaptation?.recommendation_headline
    || (appliedAutomatically ? "Apex adjusted your plan" : null)
    || (needsConfirm ? "Apex recommends an adjustment" : null);
  const recommendationReason = adaptation?.recommendation_reason || null;
  const showAutoAppliedNotice = applied && appliedAutomatically;

  const run = async (fn) => {
    if (!adaptation?.adaptation_id) return;
    setBusy(true);
    setActionError(null);
    try {
      await fn(adaptation.adaptation_id);
      notifyPlanChanged();
      if (onResolved) await onResolved();
    } catch (err) {
      const message = err?.response?.data?.detail?.message
        || err?.response?.data?.detail
        || err?.message
        || "Could not update the plan.";
      setActionError(typeof message === "string" ? message : "Could not update the plan.");
    } finally {
      setBusy(false);
    }
  };

  const showApply = Boolean((needsConfirm || incomplete) && !escalation);
  const showKeep = Boolean((showApply || (pending && escalation)) && !keepAsPlanned && !applied);
  const showDetails = !planChangePreview || planChangePreview.kind !== "multi";

  const actions = (showApply || showKeep) ? (
    <span className="flex flex-wrap gap-2">
      {showApply && (
        <button
          type="button"
          disabled={busy}
          onClick={() => run(applyPlanAdaptation)}
          data-testid="plan-change-apply"
          className="btn-primary-app px-3 py-1.5 text-[10px] tracking-widest uppercase"
        >
          Apply changes
        </button>
      )}
      {showKeep && (
        <button
          type="button"
          disabled={busy}
          onClick={() => run((id) => rejectPlanAdaptation(id, "keep_current_plan"))}
          data-testid="plan-change-keep"
          className="btn-secondary-app px-3 py-1.5 text-[10px] tracking-widest uppercase"
        >
          Keep current plan
        </button>
      )}
    </span>
  ) : null;

  const cardTitle = recommendationHeadline && (needsConfirm || showAutoAppliedNotice)
    ? recommendationHeadline
    : "Plan change";

  return (
    <CoachingSectionCard
      title={cardTitle}
      headingId="coaching-plan-change-heading"
      testId="coaching-plan-change-card"
      error={error}
      actions={actions}
      empty={noAdaptation ? "No plan changes required." : null}
    >
      {!noAdaptation && adaptation && (
        <>
          {showAutoAppliedNotice && (
            <div
              className="mb-3 rounded-md border border-analytics-green/40 bg-analytics-green/10 px-3 py-2 text-xs text-analytics-green"
              data-testid="plan-change-auto-applied-notice"
            >
              Applied automatically under Safe Recovery Auto
            </div>
          )}
          {applied && !showAutoAppliedNotice && (
            <div
              className="mb-3 rounded-md border border-analytics-green/40 bg-analytics-green/10 px-3 py-2 text-xs text-analytics-green"
              data-testid="plan-change-applied-notice"
            >
              Your plan was adjusted
            </div>
          )}
          {incomplete && !applied && (
            <div
              className="mb-3 rounded-md border border-analytics-amber/40 bg-analytics-amber/10 px-3 py-2 text-xs text-analytics-amber"
              data-testid="plan-change-incomplete-notice"
            >
              {INCOMPLETE_APPLY_MESSAGE}
            </div>
          )}
          {adaptation.block_regeneration_required && !applied && (
            <div
              className="mb-3 rounded-md border border-analytics-amber/40 bg-analytics-amber/10 px-3 py-2 text-xs text-analytics-amber"
              data-testid="plan-change-rebuild-notice"
            >
              Remaining plan needs rebuilding. This cannot be applied as a normal plan change.
            </div>
          )}
          {(needsConfirm || showAutoAppliedNotice) && !incomplete && recommendationReason && (
            <p
              className="mb-3 text-sm text-dark-text-secondary"
              data-testid="plan-change-recommend-reason"
            >
              {recommendationReason}
            </p>
          )}
          {needsConfirm && !incomplete && !recommendationReason && (
            <div
              className="mb-3 rounded-md border border-analytics-amber/40 bg-analytics-amber/10 px-3 py-2 text-xs text-analytics-amber"
              data-testid="plan-change-recommend-notice"
            >
              Endurava recommends adjusting your plan
            </div>
          )}

          {planChangePreview?.kind === "multi" && (needsConfirm || showAutoAppliedNotice) && (
            <div
              className="mb-4 rounded-md border border-dark-border/60 bg-dark-surface/40 p-3 space-y-4"
              data-testid="plan-change-preview"
            >
              {planChangePreview.changes.map((change, index) => (
                <div
                  key={`${change.dayLabel}-${index}`}
                  data-testid={`plan-change-preview-change-${index}`}
                  className="space-y-2"
                >
                  <p className="text-sm font-medium text-dark-text-primary">
                    {change.dayLabel}
                  </p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <PreviewSide
                      side={change.before}
                      label="Before"
                      testIdPrefix={`plan-change-preview-${index}`}
                    />
                    <PreviewSide
                      side={change.after}
                      label="After"
                      testIdPrefix={`plan-change-preview-${index}`}
                    />
                  </div>
                  <div data-testid={`plan-change-preview-why-${index}`}>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-dark-text-muted">
                      Why
                    </p>
                    <p className="mt-1 text-sm text-dark-text-secondary whitespace-pre-wrap break-words">
                      {change.why}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showDetails && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <CoachingMetric label="Status" value={formatAdaptationStatus(adaptation.status)} testId="plan-change-status" />
              <CoachingMetric
                label="Applied"
                value={APPLIED_LABELS[adaptation.applied_status] || adaptation.applied_status || "—"}
                testId="plan-change-applied-status"
              />
              <CoachingMetric label="Effective" value={formatDate(adaptation.effective_date, timezone)} testId="plan-change-effective" />
              <CoachingMetric label="Scope" value={formatAdaptationScope(adaptation.scope)} testId="plan-change-scope" />
            </div>
          )}

          {showDetails && Array.isArray(adaptation.actions)
              && adaptation.actions.length > 0 && (
            <ul className="mt-4 space-y-1 text-sm" data-testid="plan-change-actions">
              {adaptation.actions.map((a, idx) => (
                <li key={idx}>
                  {formatAdaptationAction(a.action)}
                </li>
              ))}
            </ul>
          )}

          {showDetails && (
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] uppercase tracking-widest text-dark-text-muted">
              <span data-testid="plan-change-affected-count">
                {adaptation.affected_future_session_ids?.length ?? 0}
                {" affected session(s)"}
              </span>
              {adaptation.reassessment_required && (
                <span className="text-analytics-amber"
                        data-testid="plan-change-reassessment-flag">
                  Reassessment required
                </span>
              )}
              {adaptation.safety_suspension && (
                <span className="text-analytics-red"
                        data-testid="plan-change-safety-flag">
                  Safety suspension
                </span>
              )}
              <span data-testid="plan-change-confidence">
                {formatConfidence(adaptation.confidence)}
              </span>
            </div>
          )}

          {actionError && (
            <p className="mt-3 text-xs text-analytics-red" data-testid="plan-change-action-error">
              {actionError}
            </p>
          )}

          {explanation && (
            <div className="mt-4 pt-4 border-t border-dark-border">
              <CoachingExplanation
                explanation={explanation}
                testId="plan-change-explanation"
              />
            </div>
          )}
        </>
      )}
    </CoachingSectionCard>
  );
};
